<template>
  <div class="app test-vocacional-container">
    <!-- Renderizado dinámico de los pasos del test -->
    <component
      :is="currentStep"
      :userData="userData"
      :scoreData="scoreData"
      :aiAnalysis="aiAnalysis"
      :answersText="answersText"
      @next-step="goToStep"
      @update-user="updateUserData"
      @test-completed="handleTestCompleted"
      @ai-analysis-completed="handleAiAnalysisCompleted"
      @restart="handleRestart"
    ></component>
  </div>
</template>

<script>
// Componentes de cada paso
import TestIntro from './TestIntro.vue';
import TestQuestions from './TestQuestions.vue';
import TestLoading from './TestLoading.vue';
import TestResults from './TestResults.vue';

export default {
  name: 'TestVocacional',
  components: {
    TestIntro,
    TestQuestions,
    TestLoading,
    TestResults
  },
  data() {
    return {
      currentStep: 'TestIntro', // TestIntro | TestQuestions | TestLoading | TestResults
      userData: {
        name: '',
        apellidos: '',
        email: '',
        celular: '',
        tipo_documento: '',
        numero_documento: '',
        genero: '',
        colegio: '',
        grado: '',
        ciudad: '',
        terms: false
      },
      scoreData: {
        scores: {},
        sorted: [],
        top1: '',
        top2: '',
        maxVal: 0
      },
      answersText: [],
      aiAnalysis: null, // Guardará la respuesta de la simulación / API
    };
  },
  methods: {
    goToStep(stepName) {
      this.currentStep = stepName;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    updateUserData(data) {
      this.userData = { ...this.userData, ...data };
    },
    handleTestCompleted(payload) {
      if (payload && payload.scoreData) {
        this.scoreData = payload.scoreData;
        this.answersText = payload.answersText;
      } else {
        this.scoreData = payload;
      }
      this.goToStep('TestLoading');
    },
    handleAiAnalysisCompleted(analysis) {
      this.aiAnalysis = analysis;
      this.goToStep('TestResults');
    },
    handleRestart() {
      this.aiAnalysis = null;
      this.scoreData = {
        scores: {},
        sorted: [],
        top1: '',
        top2: '',
        maxVal: 0
      };
      this.answersText = [];
      this.goToStep('TestIntro');
    }
  }
};
</script>

<style>
/* Estilos Globales extraídos del test_vocacional.html */
:root{
  --carbon:#4a4848;--gold:#dfc14e;--gold-d:#d0ab4b;--gold-bg:#FBF6E3;
  --white:#fff;--bg:#F8F7F3;--border:#E5E3DB;--text-sec:#7A7878;
  --shadow:0 2px 16px rgba(74,72,72,.10);
}
.test-vocacional-container * {
  box-sizing:border-box;
}
.test-vocacional-container {
  font-family:'Montserrat',sans-serif;
  background:var(--bg);
  color:var(--carbon);
  min-height:100vh;
  max-width:720px;
  margin:0 auto;
  padding:2rem 1.25rem 4rem;
}

/* ── Layout ── */
.brand-bar{display:flex;align-items:center;justify-content:space-between;margin-bottom:2rem;padding-bottom:1rem;border-bottom:2px solid var(--gold)}
.brand-bar img{height:44px;width:auto;max-width:260px}
.brand-tag{font-size:10px;font-weight:600;letter-spacing:.12em;color:var(--text-sec);text-transform:uppercase}

/* ── Cards ── */
.card{background:var(--white);border-radius:16px;padding:1.75rem;border:.5px solid var(--border);box-shadow:var(--shadow);margin-bottom:1rem}

/* ── Intro ── */
.intro-eyebrow{font-size:11px;font-weight:700;letter-spacing:.1em;color:var(--gold-d);text-transform:uppercase;margin-bottom:.75rem}
.intro-h{font-size:28px;font-weight:800;color:var(--carbon);line-height:1.2;margin-bottom:.75rem}
.intro-h span{color:var(--gold-d)}
.intro-sub{font-size:14px;color:var(--text-sec);line-height:1.7;margin-bottom:1.5rem}
.pills{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:1.75rem}
.pill{padding:5px 13px;border-radius:20px;font-size:12px;font-weight:500;border:.5px solid var(--border);color:var(--text-sec);background:var(--white)}
.pill.accent{background:var(--gold-bg);color:#633806;border-color:#dfc14e}

/* ── Form fields ── */
.field-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:1rem}
.field-grid.full{grid-template-columns:1fr}
.field{display:flex;flex-direction:column;gap:5px}
.field label{font-size:12px;font-weight:600;color:var(--carbon);letter-spacing:.03em}
.field input,.field select{padding:11px 14px;border:.5px solid var(--border);border-radius:10px;font-family:'Montserrat',sans-serif;font-size:14px;color:var(--carbon);background:var(--white);transition:border-color .2s}
.field input:focus,.field select:focus{outline:none;border-color:var(--gold)}
.field input.err{border-color:#E24B4A}
.field .hint{font-size:11px;color:#A32D2D;display:none}
.field input.err+.hint{display:block}

/* ── Buttons ── */
.btn-primary{background:var(--carbon);color:var(--white);border:none;padding:14px 32px;border-radius:10px;font-family:'Montserrat',sans-serif;font-size:14px;font-weight:700;cursor:pointer;transition:background .2s,transform .1s;letter-spacing:.02em}
.btn-primary:hover{background:#333}
.btn-primary:active{transform:scale(.98)}
.btn-primary:disabled{opacity:.5;cursor:not-allowed}
.btn-primary.gold{background:var(--gold);color:var(--carbon)}
.btn-primary.gold:hover{background:var(--gold-d)}
.btn-ghost{background:transparent;color:var(--text-sec);border:none;padding:12px 18px;font-family:'Montserrat',sans-serif;font-size:14px;cursor:pointer}
.btn-ghost:hover{color:var(--carbon)}
.btn-outline{background:var(--white);color:var(--carbon);border:1.5px solid var(--carbon);padding:12px 24px;border-radius:10px;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:.2s}
.btn-outline:hover{background:var(--carbon);color:var(--white)}

/* ── Progress ── */
.progress-wrap{margin-bottom:2rem}
.progress-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
.progress-label{font-size:12px;font-weight:600;color:var(--text-sec)}
.progress-counter{font-size:12px;font-weight:700;color:var(--carbon)}
.progress-track{height:4px;background:#E5E3DB;border-radius:2px}
.progress-fill{height:4px;background:var(--carbon);border-radius:2px;transition:width .4s ease}

/* ── Question ── */
.q-section{font-size:11px;font-weight:700;letter-spacing:.1em;color:var(--gold-d);text-transform:uppercase;margin-bottom:.6rem}
.q-text{font-size:19px;font-weight:700;color:var(--carbon);line-height:1.4;margin-bottom:1.75rem}
.options{display:flex;flex-direction:column;gap:10px}
.opt-btn{background:var(--white);border:1.5px solid var(--border);border-radius:12px;padding:14px 18px;text-align:left;cursor:pointer;font-family:'Montserrat',sans-serif;font-size:14px;color:var(--carbon);display:flex;align-items:flex-start;gap:12px;transition:border-color .15s,background .15s;line-height:1.5}
.opt-btn:hover{border-color:var(--gold);background:var(--gold-bg)}
.opt-btn.selected{border:2px solid var(--carbon);background:var(--gold-bg);font-weight:600}
.opt-icon{width:22px;height:22px;border-radius:50%;border:1.5px solid #C8C6BE;flex-shrink:0;margin-top:1px;transition:.15s;display:flex;align-items:center;justify-content:center}
.opt-btn.selected .opt-icon{background:var(--carbon);border-color:var(--carbon)}
.opt-btn.selected .opt-icon::after{content:'';width:8px;height:8px;background:var(--white);border-radius:50%}
.nav-row{display:flex;justify-content:space-between;align-items:center;margin-top:1.5rem}

/* ── Loading AI ── */
.ai-loading{text-align:center;padding:3rem 1rem}
.ai-pulse{width:56px;height:56px;border-radius:50%;background:var(--gold-bg);border:3px solid var(--gold);margin:0 auto 1rem;display:flex;align-items:center;justify-content:center;animation:pulse 1.4s ease-in-out infinite}
.ai-pulse svg{width:24px;height:24px}
@keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.12);opacity:.7}}
.ai-loading h3{font-size:17px;font-weight:700;color:var(--carbon);margin-bottom:.5rem}
.ai-loading p{font-size:13px;color:var(--text-sec);line-height:1.6;max-width:380px;margin:0 auto}
.ai-steps{display:flex;flex-direction:column;gap:6px;margin-top:1.5rem;max-width:320px;margin-left:auto;margin-right:auto}
.ai-step{display:flex;align-items:center;gap:10px;font-size:12px;color:var(--text-sec);opacity:.5;transition:opacity .4s}
.ai-step.active{opacity:1;color:var(--carbon);font-weight:500}
.ai-step.done{opacity:1;color:#1D9E75}
.step-dot{width:8px;height:8px;border-radius:50%;background:currentColor;flex-shrink:0}

/* ── Results ── */
.result-badge{display:inline-block;padding:5px 14px;border-radius:4px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;margin-bottom:1rem}
.result-name{font-size:24px;font-weight:800;color:var(--carbon);margin-bottom:.5rem;line-height:1.2}
.result-sub{font-size:14px;color:var(--text-sec);line-height:1.7;margin-bottom:1.5rem}

/* ── Score bars ── */
.score-section{margin-bottom:1.5rem}
.score-title{font-size:11px;font-weight:700;letter-spacing:.08em;color:var(--text-sec);text-transform:uppercase;margin-bottom:.75rem;padding-bottom:6px;border-bottom:1.5px solid var(--gold)}
.score-row{display:flex;align-items:center;gap:10px;margin-bottom:10px}
.score-label{font-size:12px;font-weight:500;color:var(--text-sec);width:108px;flex-shrink:0}
.score-track{flex:1;height:8px;background:#EAE8E2;border-radius:4px;overflow:hidden}
.score-fill{height:8px;border-radius:4px;transition:width .7s ease}
.score-pct{font-size:11px;font-weight:600;color:var(--text-sec);width:34px;text-align:right}

/* ── AI analysis block ── */
.analysis-block{background:var(--white);border-radius:14px;border:.5px solid var(--border);padding:1.25rem;margin-bottom:.75rem}
.analysis-block h4{font-size:13px;font-weight:700;color:var(--carbon);margin-bottom:.75rem;display:flex;align-items:center;gap:8px}
.analysis-icon{width:20px;height:20px;border-radius:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.analysis-text{font-size:13px;color:var(--text-sec);line-height:1.75}
.analysis-text strong{color:var(--carbon);font-weight:600}

/* ── Job cards ── */
.job-card{background:var(--white);border:.5px solid var(--border);border-radius:14px;padding:1.1rem 1.25rem;margin-bottom:.75rem}
.job-card.featured{border:2px solid var(--gold);background:var(--gold-bg)}
.job-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:.5rem}
.job-title{font-size:14px;font-weight:700;color:var(--carbon);line-height:1.3;flex:1;margin-right:8px}
.job-badge{font-size:10px;font-weight:700;padding:3px 8px;border-radius:20px;white-space:nowrap;flex-shrink:0}
.job-badge.hot{background:var(--gold-bg);color:#633806}
.job-badge.new{background:#EAF3DE;color:#27500A}
.job-stat{font-size:12px;color:var(--text-sec);margin-bottom:.25rem}
.job-stat strong{color:var(--carbon)}
.prog-list{display:flex;flex-direction:column;gap:7px}
.prog-item{display:flex;justify-content:space-between;align-items:center;padding:9px 12px;border-radius:10px;font-size:13px}
.prog-item.top{background:var(--gold-bg);border:.5px solid var(--gold)}
.prog-item.rest{background:#F0EEE8}
.prog-name{font-weight:500;color:var(--carbon)}
.prog-meta{font-size:11px;color:var(--text-sec)}
.prog-star{font-size:11px;font-weight:700;color:#633806}

/* ── CTA row ── */
.cta-row{display:flex;gap:10px;flex-wrap:wrap;margin-top:1rem}

/* ── Consent ── */
.consent-box{background:#F5F4F0;border:.5px solid var(--border);border-radius:12px;padding:1rem 1.25rem;margin:.75rem 0 1rem}
.consent-box.err{border-color:#E24B4A;background:#FEF4F4}
.consent-row{display:flex;align-items:flex-start;gap:12px}
.consent-check{width:18px;height:18px;border:1.5px solid #C8C6BE;border-radius:4px;flex-shrink:0;margin-top:2px;cursor:pointer;accent-color:var(--carbon);appearance:none;-webkit-appearance:none;background:var(--white);display:flex;align-items:center;justify-content:center;transition:border-color .2s,background .2s}
.consent-check:checked{background:var(--carbon);border-color:var(--carbon)}
.consent-check:checked::after{content:'';display:block;width:5px;height:9px;border:2px solid var(--white);border-top:none;border-left:none;transform:rotate(45deg) translate(-1px,-1px)}
.consent-text{font-size:12px;color:var(--text-sec);line-height:1.65}
.consent-text strong{color:var(--carbon);font-weight:600}
.consent-text a{color:var(--carbon);font-weight:600;text-decoration:underline}
.consent-err-msg{font-size:11px;color:#A32D2D;margin-top:.5rem;display:none}
.consent-box.err .consent-err-msg{display:block}

/* ── Responsive ── */
@media(max-width:520px){
  .field-grid{grid-template-columns:1fr}
  .intro-h{font-size:22px}
  .q-text{font-size:16px}
  .cta-row{flex-direction:column}
}
</style>
